import React, { useMemo, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WorkIcon from "@mui/icons-material/Work";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddIcon from "@mui/icons-material/Add";
import { Contact } from "../../../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../../../interfaces/DTO/mimshakData";
import { Mimshak } from "../../../../interfaces/DTO/mislaka_xml_models";
import { SalaryBaseAnalyzer } from "../../../../services/deposits/salaryBaseAnalyzer";
import { MimshakPensionService } from "../../../../services/mimshakPensionService";
import { formatCurrency } from "../../../../utils/namesFormater";
import { formatPolicyEmploymentStatus } from "../../../../utils/enumFormatter";
import { OccupationInfoCard } from "../../../PersonFinancialData/Insurance/Cards/OccupationInfoCard";
import EmploymentRecordCard, { EmploymentRecordKind } from "./EmploymentRecordCard";
import { useContactEmploymentSave } from "./useContactEmploymentSave";

interface FundEmploymentInfoProps {
  contact: Contact;
  /** the contact's MimshakData - the latest employer and the occupations reported in the funds */
  mimshakData: MimshakData[];
}

/** YYYYMM -> MM/YYYY */
const formatReportingPeriod = (period?: string) => (period && /^\d{6}$/.test(period) ? `${period.slice(4, 6)}/${period.slice(0, 4)}` : "");

/**
 * מעסיק אחרון לפי הקופות (ותחומי העיסוק מהכיסויים). לחיצה על הכרטיס מוסיפה את המעסיק לתעסוקה האחרונה – קודם
 * שואלים אם כשכיר או כעצמאי, ואז ממלאים את הפרטים.
 */
const FundEmploymentInfo: React.FC<FundEmploymentInfoProps> = ({ contact, mimshakData }) => {
  const save = useContactEmploymentSave(contact);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kind, setKind] = useState<EmploymentRecordKind | null>(null);

  // the latest month's employer with the highest salary base
  const latest = useMemo(() => {
    const summary = SalaryBaseAnalyzer.getSummary([contact], mimshakData)[contact.id!];
    if (!summary) return null;
    const all = [...Object.entries(summary.hishtalmut), ...Object.entries(summary.other)].flatMap(([planType, employers]) =>
      employers.map(({ period, employer, data }) => ({ employer, period, planType: Number(planType), salaryBase: data.salaryBase }))
    );
    if (all.length === 0) return null;
    const latestPeriod = all.reduce((max, c) => (parseInt(c.period) > parseInt(max) ? c.period : max), all[0].period);
    return all
      .filter((c) => c.period === latestPeriod)
      .reduce((max, c) => (c.salaryBase > max.salaryBase ? c : max));
  }, [contact, mimshakData]);

  // each Mimshak once (a contact's MimshakData rows may share one)
  const occupations = useMemo(() => {
    const mimshaks = Array.from(new Set(mimshakData.map((md) => md.data).filter((m): m is Mimshak => !!m)));
    return MimshakPensionService.extractOccupationsFromProducts(mimshaks.flatMap((m) => m.products || []));
  }, [mimshakData]);

  const alreadyAdded =
    !!latest &&
    ((contact.data?.last_employment_data ?? []).some((e) => e.employer_name?.trim() === latest.employer.trim()) ||
      (contact.data?.biz_data ?? []).some((b) => b.business_name?.trim() === latest.employer.trim()));

  const close = () => {
    setDialogOpen(false);
    setKind(null);
  };

  return (
    <>
      {latest && (
        <Box
          onClick={alreadyAdded ? undefined : () => setDialogOpen(true)}
          sx={{
            width: "100%",
            p: 2,
            borderRadius: 2,
            boxShadow: 1,
            backgroundColor: "background.default",
            cursor: alreadyAdded ? "default" : "pointer",
            border: "1px solid transparent",
            "&:hover": alreadyAdded ? undefined : { borderColor: "primary.main" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              מעסיק אחרון לפי הקופות
              {latest.period && ` · דיווח ${formatReportingPeriod(latest.period)}`}
            </Typography>
            <Typography variant="caption" color={alreadyAdded ? "success.main" : "secondary.main"} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {alreadyAdded ? (
                "הוסף לתעסוקה האחרונה"
              ) : (
                <>
                  <AddIcon sx={{ fontSize: 16 }} />
                  לחץ להוספה לתעסוקה האחרונה
                </>
              )}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={500}>
            {latest.employer}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 1, mt: 0.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                שכר בסיס אחרון
              </Typography>
              <Typography fontWeight={500}>{formatCurrency(latest.salaryBase)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                העסקה
              </Typography>
              <Typography fontWeight={500}>{formatPolicyEmploymentStatus(latest.planType) || "-"}</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {occupations.length > 0 && (
        <Box sx={{ width: "100%" }}>
          {occupations.map((occupation, index) => (
            <OccupationInfoCard key={index} occupation={occupation} />
          ))}
        </Box>
      )}

      {latest && (
        <Dialog open={dialogOpen} onClose={close} maxWidth="sm" fullWidth dir="rtl">
          <IconButton onClick={close} size="small" sx={{ position: "absolute", left: 12, top: 12 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogTitle sx={{ pl: 6 }}>הוספה לתעסוקה האחרונה</DialogTitle>
          <DialogContent>
            {!kind ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}>
                <Typography>
                  להוסיף את <strong>{latest.employer}</strong>:
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="outlined" size="large" fullWidth startIcon={<WorkIcon sx={{ ml: 1 }} />} onClick={() => setKind("employment")}>
                    כשכיר
                  </Button>
                  <Button variant="outlined" size="large" fullWidth startIcon={<StorefrontIcon sx={{ ml: 1 }} />} onClick={() => setKind("business")}>
                    כעצמאי
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ pb: 1 }}>
                <EmploymentRecordCard
                  kind={kind}
                  value={null}
                  initialDraft={
                    kind === "employment"
                      ? { employer_name: latest.employer, gross_salary: latest.salaryBase || undefined, is_active: true }
                      : { business_name: latest.employer }
                  }
                  onSave={async (record) => {
                    await save.add(kind, record);
                    close();
                  }}
                  onCancelNew={() => setKind(null)}
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default FundEmploymentInfo;
